/**
 * Shared helpers for the platform usage screens (SF-5095): type labels, sums and the link to a
 * company's Health tab.
 */
import { toPeriodString } from '../../utils/date';
import { formatNumber } from '../../utils/format';
import { SURVEY_TYPE_KEYS, type UsageTotals, type UsageTypeKey } from './usageClient';

/** How many months the usage month picker offers, this month included. */
const USAGE_MONTHS_BACK = 36;

/** The usage counts are worked out in UK time, so "this month" is the month in London. */
const USAGE_TIME_ZONE = 'Europe/London';

const londonYearMonth = new Intl.DateTimeFormat('en-GB', {
  timeZone: USAGE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
});

/**
 * This month for the usage screens, "YYYY-MM" in Europe/London: the same month the API uses
 * when no period is sent. At 2026-06-30T23:30Z it is already July in London.
 */
export function currentUsagePeriod(now: Date = new Date()): string {
  const parts = londonYearMonth.formatToParts(now);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  return `${year}-${month}`;
}

/** The months the usage month picker offers, newest first, ending this month in London. */
export function usageMonthOptions(today: Date = new Date()): string[] {
  const [year, month] = currentUsagePeriod(today).split('-').map(Number);
  return Array.from({ length: USAGE_MONTHS_BACK }, (_, i) =>
    toPeriodString(new Date(Date.UTC(year, month - 1 - i, 1))),
  );
}

/** What staff call each usage type. */
export const USAGE_TYPE_LABELS: Record<UsageTypeKey, string> = {
  audit: 'Audits',
  mystery_visit: 'Mystery visits',
  help_desk: 'Help desk',
  learning: 'Learning',
  client_connect: 'Client Connect',
  csat: 'CSAT',
  generic: 'Generic surveys',
  ratings: 'Old style ratings',
};

/**
 * The surveys figure, as the sum of the survey types. The screens show the types inside the
 * surveys figure, so it is added up from them rather than read from `surveys.total`: the two
 * can never disagree on screen. A missing type counts as 0.
 */
export function surveysTotal(totals: UsageTotals): number {
  return SURVEY_TYPE_KEYS.reduce((sum, key) => sum + (totals.surveys?.by_type?.[key] ?? 0), 0);
}

/**
 * The share of a month's feedback that came in as old style ratings, as a percent (0 to 100).
 * Worked out from the counts so the unit never depends on how the API rounds its own share.
 * Null when there was no feedback at all.
 */
export function ratingsSharePercent(ratings: number, totalFeedback: number): number | null {
  if (!Number.isFinite(ratings) || !Number.isFinite(totalFeedback) || totalFeedback <= 0) {
    return null;
  }
  return (ratings / totalFeedback) * 100;
}

/**
 * The company page with the Health tab open, named by the `tab` query param. Until the company
 * page has a Health tab it ignores the param and opens on its usual tab.
 */
export function companyHealthPath(companyId: string): string {
  return `/companies/${encodeURIComponent(companyId)}?tab=health`;
}

/** When the counts were made, as a date and time; null means not counted yet. */
export function formatComputedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Text added to the biggest drops rule when the API capped the list: ". Showing the biggest 25 of 40".
 * Empty when every match is shown or the total is missing (an older API).
 */
export function dropsCapNote(shown: number, total: number | undefined): string {
  if (typeof total !== 'number' || total <= shown) return '';
  return `. Showing the biggest ${formatNumber(shown)} of ${formatNumber(total)}`;
}
